import React from "react";
import styled, { useTheme } from "styled-components";
import { useSelector } from "react-redux";
import {
  CoinName,
  LanguageNameSpaces,
  PrecisionType,
  TopMarketsTableDataBySymbol,
} from "@shared/types";
import { ICON_TYPES } from "@shared/common/constants";
import { selectDisplayCurrency } from "@shared/slices/common.slice";
import { usePrecisions } from "@shared/utils/hooks/usePrecisions";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { DISPLAY_CURRENCIES } from "common/constants";
import IconWithFallback from "Global/IconWithFallback";
import PriceChange from "common/Charts/PriceChange";
import TextWithPrecisionAndIcon from "common/Text/TextWithPrecisionAndIcon";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import NewLookSparkline from "features/dashboard/common/NewLookSparkline.tsx";

interface NewLookTopMarketsProps {
  rows: TopMarketsTableDataBySymbol[];
  loading: boolean;
  onOpen: (symbol: CoinName) => void;
}

/**
 * The new look's market table: one compact grid with right-aligned figures,
 * the 24h change as a signed value next to a small trend line.
 */
const NewLookTopMarkets = ({
  rows,
  loading,
  onOpen,
}: NewLookTopMarketsProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.DASHBOARD);
  const { t: tCommon } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const theme = useTheme();

  return (
    <Table>
      <HeaderRow>
        <span>{t("market.header.pair")}</span>
        <End>{t("market.header.last_price")}</End>
        <End>{t("market.header.24h_change")}</End>
        <End className="hide-narrow">{t("market.header.24h_low_high")}</End>
        <End className="hide-narrow">{t("market.header.24h_volume")}</End>
      </HeaderRow>
      <LoadingSpinnerOverlay
        isLoading={loading}
        backgroundColor={theme.colors.cardBgColor}
        spinnerMarginTop={40}
      >
        {rows.length ? (
          rows.map((row) => (
            <MarketRow key={row.symbol} row={row} onOpen={onOpen} />
          ))
        ) : (
          <Empty>{loading ? "" : tCommon("empty.no_data")}</Empty>
        )}
      </LoadingSpinnerOverlay>
    </Table>
  );
};

export default React.memo(NewLookTopMarkets);

const CHANGE_STYLE = { fontWeight: 500 };
interface MarketRowProps {
  row: TopMarketsTableDataBySymbol;
  onOpen: (symbol: CoinName) => void;
}

const MarketRow = ({ row, onOpen }: MarketRowProps) => {
  const theme = useTheme();
  const displayCurrency = useSelector(selectDisplayCurrency);
  const isUsd = displayCurrency === DISPLAY_CURRENCIES.USD;
  const { getMinimumPrecision, getPrecision } = usePrecisions();
  const priceCoin = row.symbol?.split("/")[1];
  const [base, quote] = row.symbol.split("/");
  const change = (row.change || 0) * 100;
  const trend = row.graph_data?.map((point) => Number(point[1])) || [];
  const highLowPrecision = getPrecision(
    isUsd ? PrecisionType.USD_PRICE : PrecisionType.BTC_PRICE
  );

  return (
    <Row
      role="button"
      tabIndex={0}
      onClick={() => onOpen(row.symbol)}
      onKeyDown={(event) => event.key === "Enter" && onOpen(row.symbol)}
    >
      <Pair>
        <IconWithFallback
          coinOrExchangeName={row.symbol}
          iconType={ICON_TYPES.COIN}
          pxIconSize={24}
        />
        <PairName>
          {base}
          {quote && <Quote>/{quote}</Quote>}
        </PairName>
      </Pair>
      <End>
        <TextWithPrecisionAndIcon
          text={row.price}
          precision={getMinimumPrecision(
            PrecisionType.PRICE,
            row.symbol,
            row.exchanges
          )}
          iconType={ICON_TYPES.COIN}
          coinOrExchangeName={priceCoin}
          textStyle={INHERIT_TEXT}
          justify="flex-end"
        />
      </End>
      <ChangeCell>
        <Trend>
          <NewLookSparkline
            values={trend}
            height={24}
            color={change >= 0 ? theme.colors.green : theme.colors.red}
            showDot={false}
          />
        </Trend>
        <PriceChange change={change} size={12.5} style={CHANGE_STYLE} />
      </ChangeCell>
      <End className="hide-narrow">
        <Muted>
          <TextWithPrecisionAndIcon
            text={isUsd ? row.low_price[0] : row.low_price[1]}
            iconType={ICON_TYPES.DISPLAY_CURRENCY}
            precision={highLowPrecision}
            textStyle={INHERIT_TEXT}
          />
          <Slash>/</Slash>
          <TextWithPrecisionAndIcon
            text={isUsd ? row.high_price[0] : row.high_price[1]}
            iconType={ICON_TYPES.DISPLAY_CURRENCY}
            precision={highLowPrecision}
            textStyle={INHERIT_TEXT}
          />
        </Muted>
      </End>
      <End className="hide-narrow">
        <TextWithPrecisionAndIcon
          text={isUsd ? row.volume[0] : row.volume[1]}
          iconType={ICON_TYPES.DISPLAY_CURRENCY}
          precision={getPrecision(
            isUsd ? PrecisionType.USD_AMOUNT : PrecisionType.BTC_AMOUNT
          )}
          textStyle={INHERIT_TEXT}
          justify="flex-end"
        />
      </End>
    </Row>
  );
};

const INHERIT_TEXT = { color: "inherit", fontSize: "inherit" };
const COLUMNS =
  "minmax(140px, 1.3fr) minmax(90px, 1fr) minmax(150px, 1.2fr) minmax(170px, 1.5fr) minmax(110px, 1fr)";
const NARROW_COLUMNS =
  "minmax(120px, 1.3fr) minmax(90px, 1fr) minmax(140px, 1.2fr)";

const Table = styled.div`
  container-type: inline-size;
  font-variant-numeric: tabular-nums;
  @container (max-width: 780px) {
    .hide-narrow {
      display: none !important;
    }
  }
`;
const Grid = styled.div`
  display: grid;
  grid-template-columns: ${COLUMNS};
  align-items: center;
  column-gap: 16px;
  padding: 0 20px;
  @container (max-width: 780px) {
    grid-template-columns: ${NARROW_COLUMNS};
  }
  @container (max-width: 480px) {
    grid-template-columns: minmax(0, 1fr) 80px 72px;
    padding: 0 12px;
    column-gap: 8px;
  }
  @container (max-width: 300px) {
    grid-template-columns: minmax(0, 1fr) 76px 62px;
    column-gap: 6px;
  }
`;
const HeaderRow = styled(Grid)`
  height: 40px;
  border-bottom: ${(p) => p.theme.borders.lightest};
  color: ${(p) => p.theme.colors.secondaryText};
  font-size: 12px;
  font-weight: ${(p) => p.theme.typography.fontWeights.MEDIUM};
`;
const Row = styled(Grid)`
  height: 52px;
  cursor: pointer;
  color: ${(p) => p.theme.colors.primaryText};
  font-size: 13.5px;
  transition: background-color 150ms;
  &:not(:last-child) {
    border-bottom: ${(p) => p.theme.borders.lightest};
  }
  &:last-child {
    border-radius: 0 0 ${(p) => p.theme.radii.card}px
      ${(p) => p.theme.radii.card}px;
  }
  &:hover {
    background: ${(p) => p.theme.colors.cardLightBgColor};
  }
  &:focus-visible {
    outline: 2px solid ${(p) => p.theme.colors.primaryBlue};
    outline-offset: -2px;
  }
`;
const End = styled.span`
  display: flex;
  justify-content: flex-end;
  min-width: 0;
  text-align: right;
  white-space: nowrap;
`;
const Pair = styled.span`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  @container (max-width: 300px) {
    gap: 8px;
  }
`;
const PairName = styled.span`
  color: ${(p) => p.theme.colors.titleColor};
  font-weight: ${(p) => p.theme.typography.fontWeights.MEDIUM};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const Quote = styled.span`
  color: ${(p) => p.theme.colors.secondaryText};
  font-weight: ${(p) => p.theme.typography.fontWeights.NORMAL};
`;
const ChangeCell = styled.span`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  min-width: 0;
`;
const Trend = styled.span`
  @container (max-width: 480px) {
    display: none;
  }
  width: 64px;
  flex: none;
`;
const Muted = styled.span`
  display: inline-flex;
  align-items: center;
  color: ${(p) => p.theme.colors.secondaryText};
`;
const Slash = styled.span`
  margin: 0 4px;
`;
const Empty = styled.div`
  padding: 20px;
  text-align: center;
  color: ${(p) => p.theme.colors.secondaryText};
  font-size: 13px;
`;
